import { Col, DatePicker, Form, FormInstance, Row, Select } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import CustomButton from 'components/custom-button';
import DebounceSelect from 'components/debounce-select/select';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { LeaveRequestFilter, LeaveRequestStatus } from 'types/leave-request';
import { LEAVE_STATUS_LABEL } from '../helpers/leave-request-format';

type RangeValue = [Dayjs | null, Dayjs | null] | null;
type UserValue = { value: number; label: string } | undefined;

interface Props {
  onSubmit: (value: Partial<LeaveRequestFilter>) => void;
  /** Form do trang giữ, để trang đặt/xoá ô Nhân viên khi URL đổi (?userId=). */
  form?: FormInstance<LeaveRequestFilterFormValue>;
  /** Giá trị ban đầu lấy từ URL; nút "Xoá lọc" vẫn về mặc định (Chờ duyệt, tháng này). */
  preset?: Partial<LeaveRequestFilterFormValue>;
}

export interface LeaveRequestFilterFormValue {
  status?: LeaveRequestStatus;
  range: RangeValue;
  userId?: UserValue;
  assignedApproverId?: UserValue;
}

const defaultRange: [Dayjs, Dayjs] = [dayjs().startOf('month'), dayjs().endOf('month')];

function buildUserLabel(user: any): string {
  const full = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const head = full || user?.nickName || user?.username || user?.email || `User #${user?.id}`;
  const username = user?.username && user.username !== head ? ` (${user.username})` : '';
  return user?.email ? `${head}${username} — ${user.email}` : `${head}${username}`;
}

const fetchUsers = async (search: string) => {
  const query = search.trim();
  if (query.length < 2) return [];
  const res: any = await AxiosUtils.get(API.searchUserAdmin, { query, limit: 20 });
  const list: any[] = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
  return list.map((user) => ({ value: user.id, label: buildUserLabel(user) }));
};

const initialValues: LeaveRequestFilterFormValue = {
  status: LeaveRequestStatus.PENDING,
  range: defaultRange,
  userId: undefined,
  assignedApproverId: undefined,
};

const LeaveRequestFilterForm = ({ onSubmit, form: formProp, preset }: Props) => {
  const [ownForm] = Form.useForm<LeaveRequestFilterFormValue>();
  const form = formProp || ownForm;
  const LEAVE_REQUEST_FILTER_SELECT_CLASS = 'leave-request-filter-select';

  const submitValue = (value: LeaveRequestFilterFormValue) => {
    const from = value.range?.[0] ? dayjs(value.range[0]).format('YYYY-MM-DD') : undefined;
    const to = value.range?.[1] ? dayjs(value.range[1]).format('YYYY-MM-DD') : undefined;
    onSubmit({
      status: value.status,
      from,
      to,
      userId: value.userId?.value,
      assignedApproverId: value.assignedApproverId?.value,
    });
  };

  const onClearFilter = () => {
    form.setFieldsValue(initialValues as any);
    submitValue(initialValues);
  };

  return (
    <Form
      form={form}
      name="leave-request-filter"
      layout="vertical"
      onFinish={submitValue}
      initialValues={{ ...initialValues, ...preset }}
      className="custom-form leave-request-filter-form"
    >
      <Row gutter={[16, 8]}>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="status" label="Trạng thái">
            <Select
              allowClear
              placeholder="Tất cả"
              size="small"
              className={LEAVE_REQUEST_FILTER_SELECT_CLASS}
            >
              {Object.values(LeaveRequestStatus).map((status) => (
                <Select.Option key={status} value={status}>
                  {LEAVE_STATUS_LABEL[status]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xl={8} lg={10} md={12} xs={24}>
          <Form.Item name="range" label="Ngày nghỉ">
            <DatePicker.RangePicker
              allowClear
              size="small"
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              placeholder={['Từ', 'Đến']}
            />
          </Form.Item>
        </Col>
        <Col xl={5} lg={8} md={12} xs={24}>
          <Form.Item name="userId" label="Nhân viên">
            <DebounceSelect
              size="small"
              placeholder="Nhập username/email"
              fetchOptions={fetchUsers}
              style={{ width: '100%' }}
              className={LEAVE_REQUEST_FILTER_SELECT_CLASS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xl={5} lg={8} md={12} xs={24}>
          <Form.Item name="assignedApproverId" label="Người duyệt">
            <DebounceSelect
              size="small"
              placeholder="Nhập username/email"
              fetchOptions={fetchUsers}
              style={{ width: '100%' }}
              className={LEAVE_REQUEST_FILTER_SELECT_CLASS}
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <CustomButton
              bgColor="secondary-green"
              htmlType="submit"
              text="Tìm kiếm"
              icon={<SearchOutlined />}
            />
            <CustomButton
              text="Xoá lọc"
              bgColor="primary-grey"
              icon={<CloseOutlined />}
              onClick={onClearFilter}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default LeaveRequestFilterForm;
